import Big from 'big.js'
import {
  PaymentModalSteps,
  AmountSelection,
  LcdAmountSelection,
} from '../../utils/paymentModalSteps'
import { FieldValidity } from '../../../Interfaces/FieldValidity'
import {
  CashAmount,
  PaymentStartRequestBodyForEnrollments,
} from '../../../../swagger'

export type PaymentMethod = 'card' | 'bank'
export type PaymentFlow = 'licensing' | 'enrollment'

export type AmountDue = { amount: string; currencyCode: string }

export type PaymentInformation = {
  payment?: {
    amount?: { amount: string }
    total?: { amount: string }
  }
  ccFormToken?: string
}

export type BankPaymentInformation = {
  firstName: string
  lastName: string
  routingNumber: string
  bankAccountNumber: string
  confirmBankAccountNumber: string
}

export type SharedFieldProps = {
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
}

export interface EnterAmountContentProps {
  amountOption: AmountSelection
  setAmountOption: (option: AmountSelection) => void
  amountOfPayment: string
  isAmountOfPaymentValid: FieldValidity
  handleAmountOfPaymentChange: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void
  handleKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
  handleAmountInputBlur: (
    event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void
  amountDue: AmountDue
  defaultCurrencyCode: string
}

export interface EnterPaymentInformationContentProps {
  isLicensing: boolean
  isBankSelected: boolean
  isCardSelected: boolean
  amountDue: AmountDue
  paymentInformation?: PaymentInformation
  taxes: string
  totalAchDiscount: Big
  amountDueBigValue: Big
  total: Big
  sharedFieldProps: SharedFieldProps
  bankPaymentInformation: BankPaymentInformation
  validRoutingNumber: { input: boolean }
  bankAccountMatch: { input: boolean }
  bankAccountNumberMatch: string
  confirmBankAccountNumberMatch: string
  setErrorMessage: (message: string) => void
  backToBillingAddress: () => void
  setStep: (step: PaymentModalSteps) => void
}

export interface PaymentCompleteContentProps {
  isCardSelected: boolean
  isBankSelected: boolean
  isLicensing: boolean
  total: Big
  amountOfPayment: string
  isLcd: boolean
}

export interface PaymentMethodSelectionProps {
  isLicensing: boolean
  isCardSelected: boolean
  isBankSelected: boolean
  achAllowed: boolean
  achDisplayMessage?: string
  achDiscountAmount?: string
  onSelectCreditOrDebit: () => void
  onSelectBankAccount: () => void
}

export enum PaymentModalVariant {
  Enrollment = 'enrollment',
  Licensing = 'licensing',
}

interface AchDiscount {
  /** Amount of the discount provided. */
  amount: string
  /** Number of times to apply the ach discount. Determined by # Unpaid Programs - # Applied Discounts */
  applyDiscountTimes: number
  /** Choose whether to display message on the Bank Transfer option */
  displayMessage?: boolean
}

type ExpectedProgram = {
  programKey: number
  programName: string
  semesterOneStartDate: Date
  amountDue: CashAmount
  amountOwed: CashAmount
}

export interface PaymentModalProps {
  isOpen: boolean
  onClose: () => void
  amountDue: CashAmount
  amountOwed?: CashAmount
  achDiscount: AchDiscount
  variant?: PaymentModalVariant
  isLcd?: boolean
  actorKey?: number
  programKey?: number
  unpaidEnrollments?: PaymentStartRequestBodyForEnrollments[]
  programs?: ExpectedProgram[]
  loadingIdsToAddOnComplete?: string[]
}

export interface LCDContentProps {
  programs: Array<{
    programKey: number
    programName: string
    amountDue: CashAmount
    amountOwed: CashAmount
  }>
  selectedPrograms: number[]
  setSelectedPrograms: (programs: number[]) => void
}

export interface LcdProgramSelection {
  programKey: number
  amount: string
  option: 'owed' | 'due' | 'custom'
}

export interface LCDProgramSelectionProps {
  programs: ExpectedProgram[]
  selectedPrograms: number[]
  setSelectedPrograms: (programs: number[]) => void
  onChange?: (selections: LcdProgramSelection[]) => void
}

export interface LCDOptionsContentProps {
  amountOption: LcdAmountSelection
  setAmountOption: (option: LcdAmountSelection) => void
  totalAmountDue: AmountDue
  totalAmountOwed?: AmountDue
  defaultCurrencyCode: string
}
